import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { InventoryItem, UpdateInventoryItemInput } from '@/supabase/inventory/types/inventory';
import { normalizeItemName } from '@/supabase/inventory/normalizeItemName';
import { parseNonNegativeInt } from '@/components/inventory/parseNonNegativeInt';

export interface EditItemModalHandle {
  open: (item: InventoryItem) => void;
}

interface EditItemModalContainerProps {
  updateItem: (input: UpdateInventoryItemInput) => Promise<boolean>;
  isSubmitting: boolean;
}

/**
 * 상품 정보 수정 모달(상품명·판매가·부족 알림 기준 — stock은 GRANT가 없어 이 모달의 관할이
 * 아니며 입출고/조정 모달 담당. 설명문은 한 문장으로 확정, 오너 2026-07-17). 대상 상품은
 * `open(item)` 인자로만 시딩되고 imperative handle은 `open`만 노출 → 부모는 ref 호출만
 * 하므로 모달 오픈 시 부모가 재렌더되지 않는다.
 *
 * 저장 버튼은 dirty(하나라도 원래 값과 다름) && 유효할 때만 활성 — 무변경 저장은 서버 왕복만
 * 만드는 no-op이라 버튼 단계에서 차단한다. 제출은 바뀐 필드만 담는 partial patch(다른 탭이
 * 고친 무관 필드를 stale 값으로 되덮는 것 차단 — 같은 필드 동시 수정은 의도적 last-write-wins).
 * norm이 실제로 바뀌는 rename에만 매칭 의미 경고 캡션을 노출한다(정적 상시 노출은
 * 가격만 고치는 대다수 경로에 소음, 표기만 바뀌는 rename은 매칭 불변이라 거짓 양성).
 *
 * 실패 시(동명 충돌 23505 등 — 에러 토스트는 updateItem 내부 처리) 열린 채 유지해 재시도를
 * 허용한다. 제출 중에는 ESC/오버레이/X 닫힘도 거부한다(isPendingRef 기준) — 닫힘을 허용하면
 * "취소했는데 백그라운드에서 수정 성공"이 생긴다(RegisterUnmatchedItemDialogContainer와 동일
 * 계약, codexloop 등록다이얼로그 R1 수용 선례).
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 다른 모달 컨테이너 선례와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일.
 */
export const EditItemModalContainer = forwardRef<EditItemModalHandle, EditItemModalContainerProps>(
  ({ updateItem, isSubmitting }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [item, setItem] = useState<InventoryItem | null>(null);
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [threshold, setThreshold] = useState('');
    // 제출 중 표시(입력·버튼 비활성)용 state + 닫힘 거부/더블서밋 동기 가드용 ref 쌍 —
    // state만으로는 클릭과 리렌더 사이 구간에 가드가 안 걸린다(RegisterUnmatched와 동일 이유).
    const [isPending, setIsPending] = useState(false);
    const isPendingRef = useRef(false);
    // 진행 중이던 제출(await)이 끝나기 전에 모달이 닫혔다가 다른 대상으로 재오픈되면,
    // 그 응답이 새 세션의 폼/오픈 상태를 건드리지 않도록 세대를 구분한다.
    const sessionRef = useRef(0);

    const open = useCallback<EditItemModalHandle['open']>((nextItem) => {
      sessionRef.current += 1;
      setItem(nextItem);
      setName(nextItem.name);
      setPrice(String(nextItem.price));
      setThreshold(String(nextItem.low_stock_threshold));
      setIsOpen(true);
    }, []);

    useImperativeHandle(ref, () => ({ open }), [open]);

    const handleOpenChange = useCallback((next: boolean) => {
      // 제출 중 닫힘(ESC/오버레이/X) 거부 — 진행 중인 수정을 취소한 것처럼 보이게 두지 않는다.
      if (!next && isPendingRef.current) return;
      if (!next) {
        sessionRef.current += 1;
        setItem(null);
        setName('');
        setPrice('');
        setThreshold('');
      }
      setIsOpen(next);
    }, []);

    const trimmedName = name.trim();
    const parsedPrice = parseNonNegativeInt(price);
    const parsedThreshold = parseNonNegativeInt(threshold);
    const isNameValid = trimmedName.length > 0;
    const isFormValid = isNameValid && parsedPrice !== null && parsedThreshold !== null;
    // dirty 판정은 파싱값 기준(문자열 비교가 아니라 '05'≡5 같은 표기 차이를 무시) — 무변경
    // 저장은 no-op 왕복이라 버튼에서 차단.
    const isDirty =
      item !== null &&
      (trimmedName !== item.name ||
        parsedPrice !== item.price ||
        parsedThreshold !== item.low_stock_threshold);
    // 매칭 의미 캡션은 norm 기준(codexloop R1 #4 수용) — 'Blue Tee'→'bluetee' 같은 표기만
    // 바뀌는 rename은 norm_name(GENERATED)이 그대로라 매칭이 안 바뀌므로 경고가 거짓 양성이
    // 된다. dirty 판정(위)은 표시 문자열 기준 그대로 — 표기 정리도 저장할 가치는 있다.
    const isNormChanged =
      item !== null && isNameValid && normalizeItemName(trimmedName) !== item.norm_name;

    const handleSubmit = useCallback(async () => {
      if (item === null || isPendingRef.current) return;
      const nextName = name.trim();
      const nextPrice = parseNonNegativeInt(price);
      const nextThreshold = parseNonNegativeInt(threshold);
      if (nextName.length === 0 || nextPrice === null || nextThreshold === null) return;
      // 바뀐 필드만 담는 partial patch(codexloop R1 #2 수용) — 전 필드 고정 전송은 다른
      // 탭이 바꾼 필드를 이 탭의 stale 스냅샷으로 되덮는 lost-update를 만든다. 빈 patch는
      // 버튼 dirty 가드가 이미 막지만 여기서도 이중 방어(Enter 제출 경로).
      const patch: UpdateInventoryItemInput = { id: item.id };
      if (nextName !== item.name) patch.name = nextName;
      if (nextPrice !== item.price) patch.price = nextPrice;
      if (nextThreshold !== item.low_stock_threshold) patch.lowStockThreshold = nextThreshold;
      if (patch.name === undefined && patch.price === undefined && patch.lowStockThreshold === undefined) {
        return;
      }
      isPendingRef.current = true;
      setIsPending(true);
      const session = sessionRef.current;
      try {
        const success = await updateItem(patch);
        if (session !== sessionRef.current) return;
        if (success) {
          setItem(null);
          setName('');
          setPrice('');
          setThreshold('');
          setIsOpen(false);
        }
      } finally {
        isPendingRef.current = false;
        setIsPending(false);
      }
    }, [item, name, price, threshold, updateItem]);

    const isBusy = isSubmitting || isPending;

    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>상품 정보 수정</DialogTitle>
            <DialogDescription className="break-keep">
              {/* 한 문장 확정(오너 2026-07-17) — 재고 수량 관리 위치 안내는 삭제. 둘째 문장이
                  '입출고·' 뒤에서 꺾여 고아 분리를 만들던 linebreak 문제도 함께 소멸. */}
              상품명·판매가·부족 알림 기준을 수정합니다.
            </DialogDescription>
          </DialogHeader>
          {/* 데스크톱 퍼스트: Enter 제출을 form 기본 제출로 연결(RegisterUnmatched 선례),
              취소는 type=button으로 제출에서 제외. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="inventory-edit-item-name">상품명</Label>
                <Input
                  id="inventory-edit-item-name"
                  autoFocus
                  disabled={isBusy}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="inventory-edit-item-price">판매가</Label>
                  <Input
                    id="inventory-edit-item-price"
                    type="number"
                    min={0}
                    step={1}
                    disabled={isBusy}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inventory-edit-item-threshold">부족 알림 기준</Label>
                  <Input
                    id="inventory-edit-item-threshold"
                    type="number"
                    min={0}
                    step={1}
                    disabled={isBusy}
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                  />
                </div>
              </div>
              {isNormChanged && (
                // amber = 이 페이지의 '주의' 어휘(StockBadge 부족과 동일 계열) — 파괴는 아니지만
                // 데이터 연결이 바뀌는 결과를 제출 전에 알린다. 문구는 양방향 명시(codexloop R2
                // #1 수용): rename은 조인만 되돌릴 수 있고, 다른 이름이던 동안 생긴 차감/복원
                // 이력은 재적용되지 않는다 — "새 이름 소급 차감 없음"과 "기존 이름 차감분 자동
                // 복원 없음" 둘 다 말해야 정직하다.
                <p className="break-keep text-xs text-amber-600">
                  상품명을 바꾸면 입금 전 예약은 새 이름 기준으로 즉시 바뀝니다. 이미 입금된
                  주문을 새 이름으로 소급 차감하거나, 기존 이름으로 차감된 재고를 자동 복원하지는
                  않습니다.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
                취소
              </Button>
              <Button
                type="submit"
                className="bg-slate-900 text-white hover:bg-slate-800"
                disabled={isBusy || !item || !isFormValid || !isDirty}
              >
                저장
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  },
);

EditItemModalContainer.displayName = 'EditItemModalContainer';
